import { useEffect, useRef } from "react";
import { useFormState } from "@jsq/ds/forms";
import type { FormikErrors } from "formik";

/** Composite fields that render no named control of their own — the
 *  transaction-type picker, the rules cards — opt in with `data-field`. */
const FIELD_SELECTOR = "[name], [data-field]";

/** Hidden inputs are excluded: a custom select keeps its binding on one, ahead
 *  of the control the user can see. */
const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

const HIDDEN_SELECTOR = '[hidden], [aria-hidden="true"]';

/** Formik keys nested errors by path (`filterRules.groups[0].value`), while
 *  the rendered field carries the root of that path. */
const toRootField = (path: string): string => path.split(/[.[]/)[0] ?? "";

const toFieldName = (element: Element): string =>
  toRootField(element.getAttribute("name") ?? element.getAttribute("data-field") ?? "");

const toInvalidFieldNames = (errors: FormikErrors<unknown>): Set<string> =>
  new Set(
    Object.entries(errors)
      .filter(([, error]) => error != null)
      .map(([field]) => field),
  );

const toFocusTargets = (field: HTMLElement): HTMLElement[] => [
  ...(field.matches(FOCUSABLE_SELECTOR) ? [field] : []),
  ...field.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
];

/** `focus()` is silent when the target can't take it, so whether it landed is
 *  the only trustworthy answer. */
const focusElement = (element: HTMLElement): boolean => {
  if (element.closest(HIDDEN_SELECTOR)) return false;
  element.focus();
  return document.activeElement === element;
};

/** Move focus to the first field with a validation error, in render order. A
 *  field offering nowhere to land is passed over so a later one still gets it. */
const focusFirstFormError = (form: ParentNode, errors: FormikErrors<unknown>): void => {
  const invalidFields = toInvalidFieldNames(errors);
  if (invalidFields.size === 0) return;

  for (const field of form.querySelectorAll<HTMLElement>(FIELD_SELECTOR)) {
    if (!invalidFields.has(toFieldName(field))) continue;
    if (toFocusTargets(field).some(focusElement)) return;
  }
};

/** Focuses the first invalid field after a failed submit. Render inside a `Form`. */
export const FocusFirstFormErrorOnSubmit = () => {
  const { errors, isSubmitting, isValidating, submitCount } = useFormState();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const handledSubmitCount = useRef(0);

  useEffect(() => {
    // `submitCount` is bumped before validation runs, and the DS disables
    // fields while submitting, so the attempt has to settle first.
    if (submitCount === handledSubmitCount.current || isValidating || isSubmitting) return;

    // Recorded even on success, or the next error from ordinary editing would
    // read as this submit failing and steal focus.
    handledSubmitCount.current = submitCount;
    if (Object.keys(errors).length === 0) return;

    focusFirstFormError(anchorRef.current?.closest("form") ?? document, errors);
  }, [errors, isSubmitting, isValidating, submitCount]);

  return <span hidden ref={anchorRef} />;
};
